import { Injectable, isDevMode } from '@angular/core';

export interface RuntimeConfig {
  buildVersion: string;
  /**
   * Backend base url routed through the dev server proxy, e.g.
   * `https://kunde.cmicloud.ch`. Local development only; see the "Dev proxy"
   * section in the README.
   */
  devProxy?: string;
  oauth: {
    scopes: string;
    clientId: string;
    redirectUri: string;
  };
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private _config: RuntimeConfig | undefined;

  public get config(): RuntimeConfig {
    if (!this._config) {
      throw new Error('Runtime configuration not loaded');
    }
    return this._config;
  }

  /**
   * Reads the configured dev proxy target. Unlike {@link config} this never throws,
   * and always yields undefined in a production build, so a `devProxy` left in a
   * deployed `config.json` cannot redirect real traffic.
   */
  public get devProxy(): string | undefined {
    return isDevMode() ? this._config?.devProxy : undefined;
  }

  public async load(): Promise<void> {
    // Try root-level config.json; fall back to /assets/config.json if not found
    // Angular copies everything from public/ to site root
    const candidates = ['/config.json', '/assets/config.json'];
    for (const url of candidates) {
      try {
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) {
          continue;
        }
        const data = (await response.json()) as RuntimeConfig;
        if (data) {
          this._config = data;
          return;
        }
      } catch {
        // try next
      }
    }
    throw new Error('No runtime configuration file found (config.json)');
  }
}
